import { useMemo, useState } from "react";
import AppPageHeader from "../../components/Headers/AppPageHeader";
import CustomToggle from "../../components/Drawers/Fields/CustomToggle";
import EditSaveButton from "../../elements/EditSaveButton";
import TenantAPI from "../../api/api/TenantAPI";
import { useAtomValue, useSetAtom } from "jotai";
import { modulesAtom, setSchemaAtom } from "../../atoms/GlobalAtoms";
import _ from "underscore";
import { newToastEvent } from "../../utils/ToastUtil";
import RequiredTitle from "../../elements/RequiredTitle";
import { SelectPicker } from "rsuite";
import FieldUtils from "../../utils/FieldUtils";
import { Alert } from "reactstrap";
import { Module } from "../../types/TenantInterfaces";

interface Props {
  currentModule: Module;
}

const excludedReferenceFields = ["CreatedBy", "LastUpdatedBy"];

const ModuleListConfiguration = ({ currentModule }: Props) => {
  const setSchema = useSetAtom(setSchemaAtom);
  const modules = useAtomValue(modulesAtom);
  const moduleAPIName = currentModule.APIName;
  const config = currentModule?.ModuleList;

  const [isList, setIsList] = useState<boolean>(!!config?.IsModuleList);
  const [itemsModule, setItemsModule] = useState(config?.ListItemsModule || "");
  const [listField, setListField] = useState(config?.ListReferenceField || "");
  const [itemsField, setItemsField] = useState(
    config?.ItemsReferenceField || ""
  );

  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState("");
  const [editingConfig, setEditingConfig] = useState(false);

  const handleSaveListConfiguration = () => {
    if (isList && (!listField || !itemsModule)) {
      return setConfigError("Fill the required information.");
    }

    setConfigLoading(true);
    setEditingConfig(false);

    const dataSourceList = {
      isModuleList: isList,
      listItemsModule: itemsModule,
      listReferenceField: listField,
      itemsReferenceField: itemsField,
    };

    TenantAPI.modifyModuleListConfiguration(
      currentModule.APIName,
      dataSourceList
    )
      .then(
        (data) => {
          const newSchema = data.data.data.schema;
          setSchema(newSchema);
          newToastEvent(
            "success",
            "Successfully saved Module List configuration."
          );
        },
        (err) => {
          if (err.response) {
            if (err.response.data && err.response.data.message) {
              setConfigError(err.response.data.message);
              newToastEvent("error", err.response.data.message);
            } else {
              setConfigError("An unknown error occurred. Please try again.");
              newToastEvent(
                "error",
                "An unknown error occurred. Please try again."
              );
            }
          }
        }
      )
      .finally(() => setConfigLoading(false));
  };

  const availableListModuleFields = useMemo(() => {
    const availableFields: any = [];
    const module = modules?.find((module) => module.APIName === itemsModule);

    if (!module) return availableFields;

    const referenceFields = module?.Fields.filter(
      (field) =>
        field.FieldType === "SingleReference" ||
        field.FieldType === "MultiReference"
    );

    if (referenceFields.length < 1) return availableFields;

    for (let i = 0; i < referenceFields.length; i++) {
      const referenceField = referenceFields[i];
      const moduleFieldSourcesList = referenceField.SourcesList;
      const sourceListDataSources = FieldUtils.getSourceListDataSources({
        sourceList: moduleFieldSourcesList,
      });

      if (
        referenceField.FieldType === "SingleReference" &&
        !excludedReferenceFields.includes(referenceField.FieldType) &&
        (sourceListDataSources?.includes(moduleAPIName) ||
          sourceListDataSources === "Any")
      ) {
        availableFields.push(referenceField);
      }

      if (
        referenceField.FieldType === "MultiReference" &&
        !excludedReferenceFields.includes(referenceField.APIName) &&
        (sourceListDataSources?.includes(moduleAPIName) ||
          sourceListDataSources === "Any")
      ) {
        availableFields.push(referenceField);
      }
    }

    if (availableFields.length < 1) {
      setConfigError(
        "This list items data source does not have an available reference field, make sure you add the list data source in the selectable field options."
      );
    }

    return availableFields;
  }, [itemsModule]);

  const availableItemsReferenceFields = useMemo(() => {
    const availableItemsFields: any = [];
    const module = modules?.find((module) => module.APIName === itemsModule);

    if (!module) return availableItemsFields;

    const referenceFields = module?.Fields.filter(
      (field) =>
        field.FieldType === "SingleReference" ||
        field.FieldType === "MultiReference"
    );

    if (referenceFields.length < 1) return availableItemsFields;

    const excludedItemsReferenceFields = [
      "CreatedBy",
      "LastUpdatedBy",
      listField.replace(/\s/g, ""),
    ];

    for (let i = 0; i < referenceFields.length; i++) {
      const referenceField = referenceFields[i];

      if (
        (referenceField.FieldType === "MultiReference" ||
          referenceField.FieldType === "SingleReference") &&
        !excludedItemsReferenceFields.includes(referenceField.APIName)
      ) {
        availableItemsFields.push(referenceField);
      }
    }

    return availableItemsFields;
  }, [modules, listField]);

  const handleSetModuleList = (checked: boolean) => {
    if (!checked) {
      setItemsModule("");
      setListField("");
      setItemsField("");
    }
    setIsList(checked);
  };

  const handleSelectListItemsDataSource = (mApiName: string) => {
    if (mApiName === moduleAPIName) {
      return setConfigError(
        "You can't select the current data source as the List Items Data Source."
      );
    }

    if (configError) setConfigError("");

    setItemsModule(mApiName);
    setConfigError("");
  };

  const handleSelectListReferenceField = (field: string) => {
    setItemsField("");

    if (field === itemsField) {
      return setConfigError(
        "You can't select the same field for the item and for the list."
      );
    }

    if (configError) setConfigError("");

    setListField(field);
  };

  const handleSelectItemsReferenceField = (field: string) => {
    if (field === listField) {
      return setConfigError(
        "You can't select the same field for the item and for the list."
      );
    }

    if (configError) setConfigError("");

    setItemsField(field);
  };

  return (
    <div className="mt-2 py-3 mb-3 border-bottom border-lighter">
      <AppPageHeader
        size="sm"
        title={"List Settings"}
        description="Configure this data source as a List"
        customContent={
          <EditSaveButton
            isEditing={editingConfig}
            setIsEditing={setEditingConfig}
            onSave={handleSaveListConfiguration}
            isSubmitting={configLoading}
            disabled={configLoading}
          />
        }
      />
      {editingConfig && (
        <div className="border-top py-3 mt-3">
          <CustomToggle
            title="Enable List Configuration"
            description="Choosing Yes adds List capabilities to this data source."
            stateName="unique"
            value={isList}
            handleChange={(e: any) => handleSetModuleList(e.target.checked)}
            disabled={configLoading}
          />
          {isList && (
            <div className="d-flex flex-column">
              <RequiredTitle
                title="List Items Module"
                size="md"
                titleCSS="mb-0"
              />
              <small>This module will be used as the list items module.</small>
              <SelectPicker
                className="mt-2 mb-3"
                style={{ maxWidth: 400 }}
                placeholder="List Items Module"
                data={modules || []}
                labelKey="Name"
                valueKey="APIName"
                cleanable
                searchable={true}
                value={itemsModule}
                onClean={() => {
                  setItemsModule("");
                  setItemsField("");
                  setListField("");
                }}
                onSelect={handleSelectListItemsDataSource}
              />
            </div>
          )}
          {itemsModule && availableListModuleFields.length > 0 && (
            <div className="d-flex flex-column">
              <RequiredTitle
                title="List Reference Field"
                size="md"
                titleCSS="mt-3 mb-0"
              />
              <small>The field that references the selected list</small>
              <SelectPicker
                style={{ maxWidth: 400 }}
                className="mt-2 mb-3"
                placeholder="List Reference Field"
                data={availableListModuleFields}
                cleanable
                labelKey="Name"
                valueKey="APIName"
                onClean={() => {
                  setItemsField("");
                  setListField("");
                }}
                searchable={true}
                value={listField ?? ""}
                onSelect={handleSelectListReferenceField}
              />
            </div>
          )}
          {listField && (
            <div className="d-flex flex-column">
              <RequiredTitle
                title="List Items Reference Field"
                size="md"
                titleCSS="mt-3 mb-0"
              />
              <small>The field that references the selected item(s)</small>
              <SelectPicker
                className="mt-2"
                style={{ maxWidth: 400 }}
                placeholder="Items Module Field"
                data={availableItemsReferenceFields}
                labelKey="Name"
                valueKey="APIName"
                searchable={true}
                cleanable
                onClean={() => setItemsField("")}
                value={itemsField ?? ""}
                onSelect={handleSelectItemsReferenceField}
              />
            </div>
          )}
          {configError && (
            <Alert
              color="warning"
              className="font-weight-500 font-weight-500 m-2"
            >
              {configError}
            </Alert>
          )}
        </div>
      )}
    </div>
  );
};

export default ModuleListConfiguration;
